import CurrentUser from 'src/types/CurrentUser';

export function userHasEmailSuffix(
  user: CurrentUser | undefined,
  suffix: string,
): boolean {
  const emails = user?.emails ?? [];
  const normalizedSuffix = suffix.toLowerCase();
  return emails.some(
    (email) =>
      typeof email === 'string' &&
      email.toLowerCase().endsWith(normalizedSuffix),
  );
}

// DO NOT USE THIS FUNCTION, USE isGentlyAIUserInCurrentView INSTEAD
export function isGentlyAIUser(user: CurrentUser | undefined): boolean {
  return user?.isAdmin ?? false;
}

/**
 * Whether Gently admin-only diagnostic UI should be visible to whoever is
 * driving this session.
 *
 * While an admin imitates someone, `/auth/me` describes the TARGET — `isAdmin`
 * is `false` — so every surface gated on it disappears from the real admin
 * exactly when they need diagnostic tooling. `imitation` is only ever
 * populated by the admin-gated start handler, so its presence already proves
 * the driver is a Gently admin; `adminToolsWhileImitating` is that admin's
 * deliberate opt-in (off by default, so the imitated view stays faithful).
 *
 * Frontend visibility only. The server keeps authorizing every call against
 * the imitated user and the imitation guard keeps vetoing writes, so this
 * grants no capability — it only stops read-only diagnostics from vanishing.
 */
export function isAdminToolsVisible(
  user: CurrentUser | undefined,
  adminToolsWhileImitating: boolean,
): boolean {
  if (isGentlyAIUser(user)) {
    return true;
  }

  return adminToolsWhileImitating && user?.imitation !== undefined;
}

/**
 * Checks if user should be treated as a GentlyAI user based on their actual role
 * and current view mode. Admins can switch to end-user view, but end-users
 * are always treated as end-users.
 */
export function isAdmin(
  user: CurrentUser | undefined,
  viewMode: 'admin' | 'enduser' = 'admin',
): boolean {
  const actuallyAdmin = isGentlyAIUser(user);

  // If not actually an admin, always treat as end-user
  if (!actuallyAdmin) {
    return false;
  }

  // If admin but in end-user view mode, treat as end-user
  if (viewMode === 'enduser') {
    return false;
  }

  // Admin in admin view mode
  return true;
}
